import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMutation, usePaginatedQuery } from "convex/react";
import type { FunctionReturnType } from "convex/server";
import { CheckCheck, MessageSquareReply } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { ListRow } from "@/components/list/list-row";
import { useListNavigation } from "@/components/list/use-list-navigation";
import { useCommands, type AppCommand } from "@/components/shell/commands";
import {
  EmptyState,
  ListSkeleton,
  PageHeader,
  QueryBoundary,
} from "@/components/shell/page";
import { useWorkspace } from "@/components/shell/workspace-context";
import { useInboxFilterDefinitions } from "@/components/inbox/filter-definitions";
import { notificationActor, notificationText } from "@/components/inbox/notification-text";
import {
  filterValueOf,
  filtersOf,
  parseInboxSearch,
  searchOfFilterValue,
  type InboxFilters,
  type InboxSearch,
} from "@/components/inbox/search";
import { FilterBar } from "@/components/list/filter-bar";
import { useIssueStatusLabel } from "@/components/issues/issue-status";
import { TicketIdentifier } from "@/components/tickets/ticket-identifier";
import { HealthDot, useHealthLabel } from "@/components/updates/health";
import { Avatar } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { RelativeTime } from "@/components/ui/relative-time";
import { api, type Id } from "@/lib/api";
import { errorMessage } from "@/lib/i18n-errors";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/_app/$workspaceSlug/inbox")({
  // The filters live in the URL and nowhere else. Whatever the URL says,
  // this returns an inbox that can be drawn: it never throws.
  validateSearch: (search: Record<string, unknown>): InboxSearch => parseInboxSearch(search),
  component: InboxPage,
});

type Notification = FunctionReturnType<typeof api.notifications.list>["page"][number];

/**
 * What a notification is about, and where opening it leads: a ticket, an
 * issue, or the update of a project, on that project's Updates page. One of
 * them is always there; null only for a row this version of the page does
 * not know how to open.
 */
function subjectOf(notification: Notification, workspaceSlug: string) {
  const projectSlug = notification.project.slug;
  if (notification.update) {
    return {
      number: null,
      title: notification.update.excerpt,
      health: notification.update.health,
      to: "/$workspaceSlug/$projectSlug/updates" as const,
      params: { workspaceSlug, projectSlug },
      hash: `update-${notification.update._id}`,
    };
  }
  const subject = notification.ticket ?? notification.issue;
  if (!subject) return null;
  return {
    number: subject.number,
    title: subject.title,
    health: null,
    to: notification.ticket
      ? ("/$workspaceSlug/$projectSlug/tickets/$number" as const)
      : ("/$workspaceSlug/$projectSlug/issues/$number" as const),
    params: { workspaceSlug, projectSlug, number: String(subject.number) },
    hash: undefined,
  };
}

/** "Mark all as read", as the list hands it to the header. */
type ReadAll = { run: () => void };

function InboxPage() {
  const { t } = useTranslation("inbox");
  const { workspace } = useWorkspace();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const setSearch = useCallback(
    (next: InboxSearch) => void navigate({ search: next, replace: true }),
    [navigate],
  );
  const { t: tShell } = useTranslation("shell");
  // What "mark all as read" does, from the list below, while it shows
  // anything unread.
  const [readAll, setReadAll] = useState<ReadAll | null>(null);
  return (
    <>
      <PageHeader
        title={tShell("nav.inbox")}
        actions={
          readAll ? (
            <Button variant="ghost" size="sm" onClick={readAll.run}>
              <CheckCheck aria-hidden strokeWidth={1.75} className="size-3.5" />
              {t("markAllRead")}
            </Button>
          ) : null
        }
      />
      {/* The filters stay above a list that failed, and changing them tries
          again. Inset like the rows below, so the Filter button sits over
          their first column. */}
      <div className="shrink-0 border-b px-1.5 py-1.5 md:px-2.5">
        <QueryBoundary resetKey={workspace._id} fallback={() => null}>
          <InboxFilterBar search={search} onSearchChange={setSearch} />
        </QueryBoundary>
      </div>
      <QueryBoundary resetKey={`${workspace._id}:${JSON.stringify(search)}`}>
        <Inbox
          search={search}
          onSearchChange={setSearch}
          onReadAllChange={setReadAll}
        />
      </QueryBoundary>
      <span className="sr-only" aria-live="polite">
        {t("title")}
      </span>
    </>
  );
}

function InboxFilterBar({
  search,
  onSearchChange,
}: {
  search: InboxSearch;
  onSearchChange: (next: InboxSearch) => void;
}) {
  const definitions = useInboxFilterDefinitions(search);
  const value = useMemo(() => filterValueOf(search), [search]);
  return (
    <FilterBar
      definitions={definitions}
      value={value}
      onChange={(next) => onSearchChange(searchOfFilterValue(next, search))}
      onClear={() => onSearchChange({})}
    />
  );
}

/**
 * Whether "mark all as read" with these filters marks everything: none, or
 * only "unread", which keeps the same rows a sweep of unread ones reads.
 */
function marksEverything(filters: InboxFilters | undefined): boolean {
  if (!filters) return true;
  const { kind, projectId, actorId, unread } = filters;
  return !kind?.length && !projectId?.length && !actorId?.length && unread !== false;
}

function Inbox({
  search,
  onSearchChange,
  onReadAllChange,
}: {
  search: InboxSearch;
  onSearchChange: (next: InboxSearch) => void;
  onReadAllChange: (readAll: ReadAll | null) => void;
}) {
  const { t } = useTranslation("inbox");
  const { t: tShell } = useTranslation("shell");
  const { t: tErrors } = useTranslation("errors");
  const { workspace, projects } = useWorkspace();
  const navigate = useNavigate();
  const workspaceId = workspace._id;
  const scroller = useRef<HTMLDivElement>(null);
  const healthLabel = useHealthLabel();

  const narrowed = Object.keys(filterValueOf(search)).length > 0;
  const clear = useCallback(() => onSearchChange({}), [onSearchChange]);
  // Projects are named by slug in the URL and by id to the server: the
  // query waits for the projects when a filter names one.
  const waiting = search.project !== undefined && projects === undefined;
  const filters = useMemo(
    () =>
      waiting
        ? undefined
        : filtersOf(search, new Map((projects ?? []).map((project) => [project.slug, project._id]))),
    [search, projects, waiting],
  );

  const paginated = usePaginatedQuery(
    api.notifications.list,
    filters ? { workspaceId, filters } : "skip",
    { initialNumItems: 30 },
  );
  // Filters that can match nothing: no need to ask.
  const { results, status, loadMore } =
    filters === null ? { ...paginated, results: [], status: "Exhausted" as const } : paginated;

  // Read the moment it is opened: the dot goes and the sidebar count drops
  // before the server has answered.
  const markRead = useMutation(api.notifications.markRead).withOptimisticUpdate(
    (store, args) => {
      const ids = new Set<string>(args.notificationIds);
      let newlyRead = 0;
      for (const { args: pageArgs, value } of store.getAllQueries(api.notifications.list)) {
        if (!value || pageArgs.workspaceId !== workspaceId) continue;
        if (!value.page.some((row) => ids.has(row._id) && row.readAt === null)) continue;
        store.setQuery(api.notifications.list, pageArgs, {
          ...value,
          page: value.page.map((row) => {
            if (!ids.has(row._id) || row.readAt !== null) return row;
            newlyRead++;
            return { ...row, readAt: Date.now() };
          }),
        });
      }
      const unread = store.getQuery(api.notifications.unreadCount, { workspaceId });
      if (unread && newlyRead > 0 && !unread.more) {
        store.setQuery(
          api.notifications.unreadCount,
          { workspaceId },
          { ...unread, count: Math.max(0, unread.count - newlyRead) },
        );
      }
    },
  );
  // With filters on, only what they keep is marked: the lists asked with
  // the same filters, and the count is left to the server.
  const markAllRead = useMutation(api.notifications.markAllRead).withOptimisticUpdate(
    (store, args) => {
      const everything = marksEverything(args.filters);
      const marked = JSON.stringify(args.filters ?? {});
      for (const { args: pageArgs, value } of store.getAllQueries(api.notifications.list)) {
        if (!value || pageArgs.workspaceId !== workspaceId) continue;
        if (!everything && JSON.stringify(pageArgs.filters ?? {}) !== marked) continue;
        store.setQuery(api.notifications.list, pageArgs, {
          ...value,
          page: value.page.map((row) =>
            row.readAt === null ? { ...row, readAt: Date.now() } : row,
          ),
        });
      }
      if (everything) {
        store.setQuery(api.notifications.unreadCount, { workspaceId }, { count: 0, more: false });
      }
    },
  );

  const byId = useMemo(
    () => new Map<string, Notification>(results.map((row) => [row._id, row])),
    [results],
  );
  const ids = useMemo(() => results.map((row) => row._id as string), [results]);
  const hasUnread = results.some((row) => row.readAt === null);
  // Nothing on screen and more behind it: a page the filters emptied, or
  // one whose rows were all just marked read under "Unread". Carry on
  // until there is something to show or nothing left.
  useEffect(() => {
    if (results.length === 0 && status === "CanLoadMore") loadMore(30);
  }, [results.length, status, loadMore]);
  const report = (failure: unknown) => toast.error(errorMessage(failure, tErrors));

  const open = (id: string) => {
    const notification = byId.get(id);
    const subject = notification ? subjectOf(notification, workspace.slug) : null;
    if (!notification || !subject) return;
    if (notification.readAt === null) {
      markRead({ notificationIds: [notification._id as Id<"notifications">] }).catch(report);
    }
    void navigate({ to: subject.to, params: subject.params, hash: subject.hash });
  };

  // A notification is opened, not ticked: there is nothing to do to several.
  const list = useListNavigation({
    ids,
    onOpen: open,
    selectable: false,
    scrollContainer: scroller,
  });

  // The command is registered once and reads what is current when it runs.
  const readAll = useRef(() => {});
  useEffect(() => {
    readAll.current = () => {
      markAllRead(filters ? { workspaceId, filters } : { workspaceId }).catch(report);
    };
  });
  // The header's button. A list that failed or went away has nothing to mark.
  useEffect(() => {
    onReadAllChange(hasUnread ? { run: () => readAll.current() } : null);
    return () => onReadAllChange(null);
  }, [hasUnread, onReadAllChange]);
  const commands = useMemo<AppCommand[]>(
    () =>
      hasUnread
        ? [
            {
              id: "inbox.markAllRead",
              title: t("markAllRead"),
              group: "selection",
              keys: "shift+u",
              icon: CheckCheck,
              perform: () => readAll.current(),
            },
          ]
        : [],
    [hasUnread, t],
  );
  useCommands(commands);

  const statusLabel = useIssueStatusLabel();
  const words = { t, status: statusLabel };
  const actorName = (notification: Notification) => notificationActor(notification, words);

  return (
    <>
      <div ref={scroller} className="min-h-0 flex-1 overflow-y-auto">
        {status === "LoadingFirstPage" || (results.length === 0 && status !== "Exhausted") ? (
          <ListSkeleton />
        ) : results.length === 0 ? (
          narrowed ? (
            <EmptyState
              title={t("filters.noMatch")}
              action={
                <Button variant="outline" size="sm" onClick={clear}>
                  {t("filters.clear")}
                </Button>
              }
            />
          ) : (
            <EmptyState title={tShell("empty.inbox")} />
          )
        ) : (
          <div role="list" aria-label={tShell("nav.inbox")}>
            {results.map((notification) => {
              const unread = notification.readAt === null;
              const subject = subjectOf(notification, workspace.slug);
              if (!subject) return null;
              return (
                <ListRow
                  key={notification._id}
                  role="listitem"
                  {...list.rowProps(notification._id)}
                  // Nothing to do to several at once here, so no checkboxes.
                  onSelectToggle={undefined}
                  className="has-[a:focus-visible]:outline-2 has-[a:focus-visible]:-outline-offset-2 has-[a:focus-visible]:outline-ring"
                  leading={
                    <>
                      <span
                        className={cn(
                          "size-1.5 shrink-0 rounded-full",
                          unread ? "bg-brand" : "bg-transparent",
                        )}
                        role={unread ? "img" : undefined}
                        aria-label={unread ? t("unread") : undefined}
                      />
                      {notification.kind === "requester_replied" ? (
                        <span className="flex size-5 shrink-0 items-center justify-center text-muted-foreground">
                          <MessageSquareReply aria-hidden strokeWidth={1.75} className="size-4" />
                        </span>
                      ) : (
                        <Avatar
                          name={actorName(notification)}
                          image={notification.actor?.image}
                          agent={notification.actor?.agent} harness={notification.actor?.harness}
                        />
                      )}
                    </>
                  }
                  trailing={
                    <>
                      {subject.number === null ? null : (
                        <TicketIdentifier
                          projectKey={notification.project.key}
                          number={subject.number}
                          className="max-sm:hidden"
                        />
                      )}
                      <RelativeTime value={notification._creationTime} />
                    </>
                  }
                >
                  {/* A real link, so a notification can be opened without J
                      and K. Its focus ring is drawn on the row. */}
                  <Link
                    to={subject.to}
                    params={subject.params}
                    hash={subject.hash}
                    onClick={() => {
                      if (unread) markRead({ notificationIds: [notification._id] }).catch(report);
                    }}
                    className={cn(
                      "focus-visible:outline-none",
                      unread ? "font-medium" : "text-muted-foreground",
                    )}
                  >
                    {notificationText(notification, words)}
                  </Link>
                  {subject.health ? (
                    <HealthDot
                      health={subject.health}
                      label={healthLabel(subject.health)}
                      className="mx-2 inline-block align-middle"
                    />
                  ) : null}
                  <span className={cn("text-muted-foreground", !subject.health && "ml-2")}>
                    {subject.title}
                  </span>
                </ListRow>
              );
            })}
            {status === "CanLoadMore" || status === "LoadingMore" ? (
              <div className="px-4 py-2 md:px-6">
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={status === "LoadingMore"}
                  onClick={() => loadMore(30)}
                >
                  {t("loadMore")}
                </Button>
              </div>
            ) : null}
          </div>
        )}
      </div>
    </>
  );
}
